import React, { useEffect, useMemo, useState } from "react";
import { TrendingUp, TrendingDown, CreditCard, Sigma } from "lucide-react";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../firebase";
import { usePanelTheme } from "../context/themeContext";
import StatCard from "../components/StatCard";

function formatCurrency(value) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);
}

function BreakdownCard({ title, subtitle, items, max, labelMap, formatValue, currentTheme, theme }) {
  return (
    <div className={`rounded-3xl border p-5 shadow-sm ${currentTheme.surface}`}>
      <h3 className={`text-lg font-black ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>{title}</h3>
      <p className={`mt-1 text-sm ${currentTheme.muted}`}>{subtitle}</p>

      <div className="mt-5 space-y-3">
        {items.length ? (
          items.map(([key, value]) => {
            const width = Math.max(8, (Number(value || 0) / Math.max(max, 1)) * 100);
            return (
              <div key={key} className={`rounded-2xl border p-4 ${currentTheme.surfaceSoft}`}>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className={`text-sm font-semibold ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>
                    {labelMap[key] || key}
                  </div>
                  <div className={`text-sm font-bold ${currentTheme.muted}`}>{formatValue(value)}</div>
                </div>
                <div className={`h-2 w-full overflow-hidden rounded-full ${theme === "dark" ? "bg-slate-800" : "bg-slate-200"}`}>
                  <div className="h-full rounded-full bg-blue-500" style={{ width: `${width}%` }} />
                </div>
              </div>
            );
          })
        ) : (
          <div className={`py-8 text-sm ${currentTheme.muted}`}>No data</div>
        )}
      </div>
    </div>
  );
}

export default function Reports({ t = {}, lang = "de" }) {
  const { currentTheme, theme } = usePanelTheme();
  const [incomeItems, setIncomeItems] = useState([]);
  const [expenseItems, setExpenseItems] = useState([]);
  const [paymentItems, setPaymentItems] = useState([]);
  const [materialItems, setMaterialItems] = useState([]);
  const [workerItems, setWorkerItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const isGerman = lang === "de";

  useEffect(() => {
    const loadAllData = async () => {
      try {
        setLoading(true);
        const [incomeSnap, expensesSnap, paymentsSnap, materialsSnap, workersSnap] = await Promise.all([
          getDocs(query(collection(db, "income"), orderBy("createdAt", "desc"), limit(100))),
          getDocs(query(collection(db, "expenses"), orderBy("createdAt", "desc"), limit(100))),
          getDocs(query(collection(db, "payments"), orderBy("createdAt", "desc"), limit(100))),
          getDocs(query(collection(db, "materials"), orderBy("createdAt", "desc"), limit(100))),
          getDocs(query(collection(db, "workers"), orderBy("createdAt", "desc"), limit(100))),
        ]);

        setIncomeItems(incomeSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setExpenseItems(expensesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setPaymentItems(paymentsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setMaterialItems(materialsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setWorkerItems(workersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error("Error loading reports:", error);
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, []);

  const totals = useMemo(() => {
    const totalIncome = incomeItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    const totalExpenses = expenseItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    const totalPayments = paymentItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    return {
      totalIncome,
      totalExpenses,
      totalPayments,
      result: totalIncome - totalExpenses - totalPayments,
    };
  }, [incomeItems, expenseItems, paymentItems]);

  const expenseBreakdown = useMemo(() => {
    const bucket = expenseItems.reduce((acc, item) => {
      const key = item.category || "Other";
      acc[key] = (acc[key] || 0) + (Number(item.amount) || 0);
      return acc;
    }, {});
    return Object.entries(bucket).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [expenseItems]);

  const paymentBreakdown = useMemo(() => {
    const bucket = paymentItems.reduce((acc, item) => {
      const key = item.category || "Other";
      acc[key] = (acc[key] || 0) + (Number(item.amount) || 0);
      return acc;
    }, {});
    return Object.entries(bucket).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [paymentItems]);

  const materialStatusBreakdown = useMemo(() => {
    const bucket = materialItems.reduce((acc, item) => {
      const key = item.status || "InStock";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(bucket).sort((a, b) => b[1] - a[1]);
  }, [materialItems]);

  const workerStatusBreakdown = useMemo(() => {
    const bucket = workerItems.reduce((acc, item) => {
      const key = item.status || "Active";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(bucket).sort((a, b) => b[1] - a[1]);
  }, [workerItems]);

  const expenseCategoryLabels = {
    Material: t.expenseCategoryMaterial || "Material",
    Transport: t.expenseCategoryTransport || "Transport",
    Salary: t.expenseCategorySalary || (isGerman ? "Lohn" : "Pagë"),
    Tools: t.expenseCategoryTools || (isGerman ? "Werkzeuge" : "Vegla"),
    Other: t.expenseCategoryOther || (isGerman ? "Sonstige" : "Tjetër"),
  };

  const paymentCategoryLabels = {
    Supplier: t.paymentCategorySupplier || (isGerman ? "Lieferant" : "Furnitor"),
    Worker: t.paymentCategoryWorker || (isGerman ? "Mitarbeiter" : "Punëtor"),
    Rent: t.paymentCategoryRent || (isGerman ? "Miete" : "Qira"),
    Service: t.paymentCategoryService || (isGerman ? "Service" : "Shërbim"),
    Other: t.paymentCategoryOther || (isGerman ? "Sonstige" : "Tjetër"),
  };

  const materialStatusLabels = {
    InStock: t.materialStatusInStock || (isGerman ? "Auf Lager" : "Në stok"),
    LowStock: t.materialStatusLowStock || (isGerman ? "Wenig Bestand" : "Stok i ulët"),
    Ordered: t.materialStatusOrdered || (isGerman ? "Bestellt" : "Porositur"),
  };

  const workerStatusLabels = {
    Active: t.workerStatusActive || (isGerman ? "Aktiv" : "Aktiv"),
    Inactive: t.workerStatusInactive || (isGerman ? "Inaktiv" : "Jo aktiv"),
    Vacation: t.workerStatusVacation || (isGerman ? "Urlaub" : "Pushim"),
  };

  const maxExpenseBreakdown = Math.max(...expenseBreakdown.map(([, value]) => Number(value) || 0), 1);
  const maxPaymentBreakdown = Math.max(...paymentBreakdown.map(([, value]) => Number(value) || 0), 1);
  const maxMaterialBreakdown = Math.max(...materialStatusBreakdown.map(([, value]) => Number(value) || 0), 1);
  const maxWorkerBreakdown = Math.max(...workerStatusBreakdown.map(([, value]) => Number(value) || 0), 1);

  return (
    <div className="space-y-6">
      <div>
        <h2 className={`text-2xl font-bold tracking-tight ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>
          {t.reportsTitle || (isGerman ? "Berichte" : "Raportet")}
        </h2>
        <p className={`mt-1 text-sm ${currentTheme.muted}`}>
          {t.reportsSubtitle ||
            (isGerman
              ? "Finanz-, Material- und Mitarbeiterübersicht auf einen Blick."
              : "Pasqyrë financiare, materiali dhe punëtorësh në një vend.")}
        </p>
      </div>

      {loading ? (
        <div className={`rounded-3xl border p-10 text-sm ${currentTheme.surface}`}>
          <span className={currentTheme.muted}>{t.loading || (isGerman ? "Wird geladen..." : "Duke u ngarkuar...")}</span>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2.5 xl:grid-cols-4">

            <StatCard
              title={isGerman ? "Gesamteinnahmen" : "Totali i hyrjeve"}
              value={formatCurrency(totals.totalIncome)}
              subtitle={isGerman ? "Alle Einnahmen" : "Të gjitha hyrjet"}
              icon={TrendingUp}
              accent="emerald"
            />
            <StatCard
              title={isGerman ? "Gesamtausgaben" : "Totali i shpenzimeve"}
              value={formatCurrency(totals.totalExpenses)}
              subtitle={isGerman ? "Alle Ausgaben" : "Të gjitha shpenzimet"}
              icon={TrendingDown}
              accent="amber"
            />
            <StatCard
              title={isGerman ? "Gesamtzahlungen" : "Totali i pagesave"}
              value={formatCurrency(totals.totalPayments)}
              subtitle={isGerman ? "Alle Zahlungen" : "Të gjitha pagesat"}
              icon={CreditCard}
              accent="blue"
            />
            <StatCard
              title={isGerman ? "Ergebnis" : "Rezultati"}
              value={formatCurrency(totals.result)}
              subtitle={isGerman ? "Einnahmen - Ausgaben - Zahlungen" : "Hyrje - Shpenzime - Pagesa"}
              icon={Sigma}
              accent="violet"
            />
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            <BreakdownCard
              title={t.expenseBreakdownTitle || (isGerman ? "Ausgaben nach Kategorie" : "Shpenzime sipas kategorisë")}
              subtitle={t.expenseBreakdownSubtitle || (isGerman ? "Top-Kategorien nach Wert" : "Kategoritë kryesore sipas vlerës")}
              items={expenseBreakdown}
              max={maxExpenseBreakdown}
              labelMap={expenseCategoryLabels}
              formatValue={formatCurrency}
              currentTheme={currentTheme}
              theme={theme}
            />
            <BreakdownCard
              title={t.paymentBreakdownTitle || (isGerman ? "Zahlungen nach Kategorie" : "Pagesat sipas kategorisë")}
              subtitle={t.paymentBreakdownSubtitle || (isGerman ? "Top-Kategorien nach Wert" : "Kategoritë kryesore sipas vlerës")}
              items={paymentBreakdown}
              max={maxPaymentBreakdown}
              labelMap={paymentCategoryLabels}
              formatValue={formatCurrency}
              currentTheme={currentTheme}
              theme={theme}
            />
            <BreakdownCard
              title={t.materialStatusTitle || (isGerman ? "Materialstatus" : "Statusi i materialeve")}
              subtitle={t.materialStatusSubtitle || (isGerman ? "Anzahl pro Status" : "Numri sipas statusit")}
              items={materialStatusBreakdown}
              max={maxMaterialBreakdown}
              labelMap={materialStatusLabels}
              formatValue={(value) => value}
              currentTheme={currentTheme}
              theme={theme}
            />
            <BreakdownCard
              title={t.workerStatusTitle || (isGerman ? "Mitarbeiterstatus" : "Statusi i punëtorëve")}
              subtitle={t.workerStatusSubtitle || (isGerman ? "Anzahl pro Status" : "Numri sipas statusit")}
              items={workerStatusBreakdown}
              max={maxWorkerBreakdown}
              labelMap={workerStatusLabels}
              formatValue={(value) => value}
              currentTheme={currentTheme}
              theme={theme}
            />
          </div>
        </>
      )}
    </div>
  );
}
